import { useEffect, useMemo } from 'react'

import { Spinner } from '@factorialco/f0-react/dist/experimental'
import {
  AttendanceEntitiesCompensationBlockCompensationPoolEnum,
  AttendanceEntitiesCompensationBlockStatusEnum,
  AttendanceSidepanelCompensationBlockFragment,
  FetchCompensationOverviewSidepanelDocument,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { useQuery } from 'modules/core/hooks/useQuery'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import EmptyState from 'modules/attendance/components/ClockIn/TabsBreakdown/CompensationPannel/TimeCategoriesBreakdown/EmptyState'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import CompensationBlockUtils from 'modules/attendance/lib/utils/CompensationBlockUtils'

import SummaryCard from './SummaryCard'
import TimeBlockEntry from './TimeBlockEntry'
import TimeBlockGroup from './TimeBlockGroup'
import { useSelectionManager } from './useSelectionManager'

type Props = {
  setSelectedCompensationBlocks: React.Dispatch<
    React.SetStateAction<AttendanceSidepanelCompensationBlockFragment[]>
  >
  employeeId: number
  useComplementaryHoursLabels?: boolean
}

const COMPENSATION_POOLS: AttendanceEntitiesCompensationBlockCompensationPoolEnum[] = [
  'payroll',
  'timeoff',
  'balance',
]

const COMPENSATION_STATUSES: AttendanceEntitiesCompensationBlockStatusEnum[] = [
  'current_pending',
  'previous_pending',
  'available',
  'compensated',
]

const compensationTypeTranslated = (
  compensationPool: AttendanceEntitiesCompensationBlockCompensationPoolEnum
) => {
  const translations: Record<AttendanceEntitiesCompensationBlockCompensationPoolEnum, string> = {
    payroll: i18n.t('clock_in.sidepanel.compensation_overview.compensation_types.payroll'),
    timeoff: i18n.t('clock_in.sidepanel.compensation_overview.compensation_types.time_off'),
    balance: i18n.t('clock_in.sidepanel.compensation_overview.compensation_types.balance'),
  }

  return translations[compensationPool] || compensationPool
}

const getTimeBlockLabel = (
  block: AttendanceSidepanelCompensationBlockFragment,
  useComplementaryHoursLabels: boolean
) => {
  const name = CompensationBlockUtils.name(block)

  if (name === 'extra_hours_from_bank_of_hours') {
    return i18n.t('clock_in.sidepanel.compensation_overview.extra_hours_from_bank_of_hours')
  }

  if (name === 'extra_hours') {
    return i18n.t('clock_in.sidepanel.compensation_overview.extra_hours')
  }

  return CompensationBlockUtils.adjustCategoryNameForEmployeeType(name, useComplementaryHoursLabels)
}

const Overview = ({
  setSelectedCompensationBlocks,
  employeeId,
  useComplementaryHoursLabels: useComplementaryHoursLabelsProp,
}: Props) => {
  const selectionManager = useSelectionManager()
  const { stringDateFrom: startOn, stringDateTo: endOn } = useAttendanceDateNavigator()

  // Only use timesheet context if prop is not provided (when used from ClockIn/timesheet context)
  // When prop is provided (from TimeTrackingCollection), we skip the context to avoid errors
  const timesheetData = useTimesheetData()
  const hasComplementaryHoursFeature =
    TimesheetFeatures.COMPLEMENTARY_HOURS in (timesheetData.featureByName ?? {})
  const useComplementaryHoursLabelsFromContext =
    hasComplementaryHoursFeature &&
    timesheetData.isPartTime &&
    !!timesheetData.currentEmployeesSetting?.enableComplementaryHours
  const useComplementaryHoursLabels =
    useComplementaryHoursLabelsProp ?? useComplementaryHoursLabelsFromContext

  const { data, loading } = useQuery(FetchCompensationOverviewSidepanelDocument, {
    variables: {
      employeeId,
      endOn,
      startOn,
    },
  })

  const renderTimeBlockEntries = (blocks: AttendanceSidepanelCompensationBlockFragment[]) => {
    return blocks.map((entry) => {
      const isSelectable = entry.minutes > 0
      return (
        <TimeBlockEntry
          key={entry.id}
          label={getTimeBlockLabel(entry, useComplementaryHoursLabels)}
          note={
            CompensationBlockUtils.isPayroll(entry) ? CompensationBlockUtils.multiplier(entry) : ''
          }
          hours={minutesToText(entry.minutes)}
          id={entry.id}
          selectable={isSelectable}
        />
      )
    })
  }

  const isCompensationPool = (
    key: string
  ): key is AttendanceEntitiesCompensationBlockCompensationPoolEnum => {
    return COMPENSATION_POOLS.some((pool) => pool === key)
  }

  const renderTimeBlockGroups = (
    blocksByType: Record<string, AttendanceSidepanelCompensationBlockFragment[]>
  ) => {
    return Object.keys(blocksByType)
      .filter(isCompensationPool)
      .map((compensationPool) => {
        const blocks = blocksByType[compensationPool]
        const title = compensationTypeTranslated(compensationPool)

        return (
          <TimeBlockGroup
            key={compensationPool}
            title={title}
            totalHours={minutesToText(CompensationBlockUtils.calculateTotalMinutes(blocks))}
          >
            {renderTimeBlockEntries(blocks || [])}
          </TimeBlockGroup>
        )
      })
  }

  // Memoized calculations
  const compensationData = useMemo(() => {
    const compensationBlocks = data?.attendance.compensationBlocksConnection.nodes
    const compensationBlocksByStatus = groupBy(compensationBlocks, 'status')

    const previousPending = compensationBlocksByStatus.previous_pending
    const currentPending = compensationBlocksByStatus.current_pending
    const available = compensationBlocksByStatus.available
    const compensated = compensationBlocksByStatus.compensated

    return {
      previous_pending: {
        title: i18n.t('clock_in.sidepanel.compensation_overview.pending_compensations'),
        dotColor: '#5596F6',
        selectable: true,
        blocks: previousPending,
        totalHours: minutesToText(CompensationBlockUtils.calculateTotalMinutes(previousPending)),
        blocksByType: groupBy(previousPending, 'compensationPool'),
        tooltip: {
          label: i18n.t('clock_in.sidepanel.compensation_overview.tooltip.pending_label'),
          description: i18n.t(
            'clock_in.sidepanel.compensation_overview.tooltip.pending_description'
          ),
        },
      },
      current_pending: {
        title: i18n.t('clock_in.sidepanel.compensation_overview.pending_compensations'),
        dotColor: '#5596F6',
        selectable: true,
        blocks: currentPending,
        totalHours: minutesToText(CompensationBlockUtils.calculateTotalMinutes(currentPending)),
        blocksByType: groupBy(currentPending, 'compensationPool'),
        tooltip: {
          label: i18n.t('clock_in.sidepanel.compensation_overview.tooltip.pending_label'),
          description: i18n.t(
            'clock_in.sidepanel.compensation_overview.tooltip.pending_description'
          ),
        },
      },
      available: {
        title: i18n.t('clock_in.sidepanel.compensation_overview.available_compensations'),
        dotColor: '#F5A51C',
        selectable: true,
        blocks: available,
        totalHours: minutesToText(CompensationBlockUtils.calculateTotalMinutes(available)),
        blocksByType: groupBy(available, 'compensationPool'),
        tooltip: {
          label: i18n.t('clock_in.sidepanel.compensation_overview.tooltip.available_label'),
          description: i18n.t(
            'clock_in.sidepanel.compensation_overview.tooltip.available_description'
          ),
        },
      },
      compensated: {
        title: i18n.t('clock_in.sidepanel.compensation_overview.compensated'),
        dotColor: '#07A2AD',
        selectable: false,
        blocks: compensated,
        totalHours: minutesToText(CompensationBlockUtils.calculateTotalMinutes(compensated)),
        blocksByType: groupBy(compensated, 'compensationPool'),
        tooltip: {
          label: i18n.t('clock_in.sidepanel.compensation_overview.tooltip.compensated_label'),
          description: i18n.t(
            'clock_in.sidepanel.compensation_overview.tooltip.compensated_description'
          ),
        },
      },
    }
  }, [data])

  // Sync the selection state with the parent component
  useEffect(() => {
    const compensationBlocks = data?.attendance.compensationBlocksConnection.nodes ?? []
    const selectableBlocks = compensationBlocks.filter((block) => block.minutes > 0)
    const selectableIds = new Set(selectableBlocks.map((block) => block.id))
    const deselectIds = [...selectionManager.selectedIds].filter((id) => !selectableIds.has(id))
    if (deselectIds.length > 0) {
      selectionManager.deselectAll(deselectIds)
    }
    const selectedCompensationBlocks = compensationBlocks.filter(
      (block) => selectionManager.selectedIds.has(block.id) && block.minutes > 0
    )

    setSelectedCompensationBlocks(selectedCompensationBlocks)
  }, [selectionManager.selectedIds, setSelectedCompensationBlocks, data])

  const renderSummaryCard = (status: AttendanceEntitiesCompensationBlockStatusEnum) => {
    const statusData = compensationData[status]

    if (!statusData.blocks || statusData.blocks.length === 0) return null

    return (
      <SummaryCard
        title={statusData.title}
        dateRange={CompensationBlockUtils.getCompensationDateRange({
          startOn,
          endOn,
          blocks: statusData.blocks,
        })}
        totalHours={statusData.totalHours}
        dotColor={statusData.dotColor}
        selectable={statusData.selectable}
        selectionManager={statusData.selectable ? selectionManager : undefined}
        tooltip={statusData.tooltip}
      >
        {renderTimeBlockGroups(statusData.blocksByType)}
      </SummaryCard>
    )
  }

  const compensationPeriodStatus =
    data?.attendance.compensationPeriodStatusesConnection.nodes?.[0]?.status

  if (loading)
    return (
      <div className="flex items-center justify-center p-4">
        <Spinner />
      </div>
    )

  if (compensationPeriodStatus === 'incompatible_period') {
    return (
      <EmptyState
        header={i18n.t('clock_in.sidepanel.compensation_overview.not_compatiable_period_header')}
        body={i18n.t('clock_in.sidepanel.compensation_overview.not_compatiable_period_body')}
      />
    )
  }

  if (compensationPeriodStatus === 'in_progress') {
    return (
      <EmptyState
        header={i18n.t('clock_in.sidepanel.compensation_overview.in_progress_header')}
        body={i18n.t('clock_in.sidepanel.compensation_overview.in_progress_body')}
      />
    )
  }

  const emptyState = data?.attendance.compensationBlocksConnection.nodes?.length === 0

  if (emptyState)
    return (
      <EmptyState
        header={i18n.t('clock_in.sidepanel.compensation_overview.empty_state_header')}
        body={i18n.t('clock_in.sidepanel.compensation_overview.empty_state_body')}
      />
    )

  return <>{COMPENSATION_STATUSES.map((status) => renderSummaryCard(status))}</>
}

export default Overview
